import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Bell,
  FileText,
  History,
  Inbox,
  LogOut,
  MessageSquare,
  Package,
  Plus,
  Search,
  Settings,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import Logo from '@/components/common/Logo';

const groups = [
  {
    title: 'Business',
    links: [
      { to: '/admin/finance', label: 'Finance', icon: Wallet },
      { to: '/admin/leads', label: 'Lead Inbox', icon: Inbox },
      { to: '/portal/admin', label: 'Client Portal', icon: Users },
      { to: '/ops/orders', label: 'Stand Orders', icon: Package },
    ],
  },
  {
    title: 'Website',
    links: [
      { to: '/admin/dashboard', label: 'Website Overview', icon: BarChart3 },
      { to: '/admin/blogs', label: 'Blog Posts', icon: FileText },
      { to: '/admin/ai-writer', label: 'AI Writer', icon: Sparkles },
      { to: '/admin/testimonials', label: 'Testimonials', icon: MessageSquare },
      { to: '/admin/seo-audit', label: 'SEO Audits', icon: Search },
    ],
  },
  {
    title: 'System',
    links: [
      { to: '/admin/agent-health', label: 'Agent Health', icon: Activity },
      { to: '/admin/activity-logs', label: 'Activity Logs', icon: History },
      { to: '/admin/notifications', label: 'Notifications', icon: Bell },
      { to: '/admin/api-keys', label: 'API Keys', icon: Settings },
    ],
  },
];
export default function AdminSidebar({
  user,
  onSignOut,
}: {
  user?: User | null;
  onSignOut?: () => void | Promise<void>;
}) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <aside className="flex w-full shrink-0 flex-col border-b border-white/10 bg-slate-950 text-white md:sticky md:top-0 md:h-screen md:w-60 md:border-b-0 md:border-r">
      <div className="border-b border-white/10 p-4">
        <Logo className="h-8 w-auto" />
        {user && (
          <p className="mt-3 truncate text-xs text-slate-400">{user.email}</p>
        )}
      </div>
      <button
        className="px-4 py-3 text-left text-sm text-slate-300 md:hidden"
        aria-expanded={menuOpen}
        aria-controls="admin-menu"
        onClick={() => setMenuOpen(open => !open)}
      >
        Admin menu {menuOpen ? '−' : '+'}
      </button>
      <nav
        id="admin-menu"
        aria-label="Admin navigation"
        className={`${menuOpen ? 'block' : 'hidden'} flex-1 overflow-y-auto p-3 md:block`}
      >
        <div className="grid gap-4 sm:grid-cols-3 md:grid-cols-1">
          {groups.map(group => (
            <section key={group.title}>
              <h2 className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                {group.title}
              </h2>
              <ul className="space-y-1">
                {group.links.map(link => {
                  const active =
                    pathname === link.to ||
                    pathname.startsWith(`${link.to}/`) ||
                    (link.to === '/admin/blogs' &&
                      pathname.startsWith('/admin/blog/')) ||
                    (link.to === '/admin/api-keys' &&
                      pathname === '/admin/settings');
                  return (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        reloadDocument={link.to === '/admin/finance'}
                        aria-current={active ? 'page' : undefined}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${active ? 'bg-sky-400/10 text-sky-300' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
                      >
                        <link.icon size={17} />
                        <span>{link.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </nav>
      <div
        className={`${menuOpen ? 'flex' : 'hidden'} gap-2 border-t border-white/10 p-3 md:flex md:flex-col`}
      >
        <Link
          to="/admin/blog/new"
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
        >
          <Plus size={17} />
          New blog post
        </Link>
        {onSignOut && (
          <button
            onClick={onSignOut}
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
          >
            <LogOut size={17} />
            Sign Out
          </button>
        )}
      </div>
    </aside>
  );
}
